import { Dispatch, SetStateAction, useState } from "react";
import { Alert, Button, Col, Row } from "reactstrap";

import useCommentParams from "../Drawers/CommentsDrawer/useCommentParams";
import RichTextEditorV2 from "../CRM/RichTextEditorV2";
import useCreateComment from "../../api/api-hooks/comments/useCreateComment";
import StrUtils from "../../utils/StrUtils";
import useFetchComments from "../../api/api-hooks/comments/useFetchComments";
import IconButton from "../../elements/IconButton";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import {
  BrieComposeBody,
  BrieComposePage,
} from "../Widgets/RichText/BrieComposeDrawer";

/**
 * Comments Widget
 */
const CommentsWidget = ({
  recordID,
  recordName,
  moduleAPIName,
  setOpenRecordCommentsDrawer,
  setKeepOpen,
  onOpenBrieCompose,
  onCloseBrieCompose,
}: {
  recordID: string;
  recordName: string;
  moduleAPIName: string;
  setOpenRecordCommentsDrawer: Dispatch<SetStateAction<boolean>>;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  /** Function called when open the Brie Command component */
  onOpenBrieCompose?: () => void;
  /** Function called when close the Brie Command component */
  onCloseBrieCompose?: () => void;
}) => {
  const { commentPages } = useFetchComments("Record", recordID, !open, false);
  const [richText, setRichText] = useState("");
  const [error, setError] = useState("");
  const { openComments } = useCommentParams();
  const { createComment, creatingComment } = useCreateComment();
  const [hide, setHide] = useState(false);
  const [page, setPage] = useState<BrieComposePage>("Selection");
  const [generatedText, setGeneratedText] = useState("");

  return (
    <>
      <div
        className="cursor-pointer py-3 text-dark"
        style={{ maxWidth: 400, minWidth: 350, width: "100vw" }}
      >
        <Row className="pb-3">
          <Col
            className="d-flex justify-content-between align-items-center"
            xs=""
          >
            <div className="d-flex justify-content-center align-items-center">
              <h5 className="text-dark m-0 pr-2">
                {hide ? (page !== "Selection" ? page : "Comments") : "Comments"}
              </h5>
            </div>
            <div>
              {page === "Selection" && hide && (
                <Button
                  size="sm"
                  onClick={() => {
                    callFunctionIfExists(onCloseBrieCompose);
                    setPage("Selection");
                    setGeneratedText("");
                    setHide(false);
                  }}
                  color="danger"
                  outline
                >
                  Cancel
                </Button>
              )}
              {page !== "Selection" && (
                <>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPage("Selection");
                      setGeneratedText("");
                    }}
                  >
                    Go Back
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      const finalText = generatedText.replace(
                        /\r?\n/g,
                        "<br />"
                      );
                      setRichText(finalText);
                      callFunctionIfExists(onCloseBrieCompose);
                      setHide(false);
                      setPage("Selection");
                    }}
                    disabled={!generatedText}
                    style={{ width: 80 }}
                  >
                    Insert
                  </Button>
                </>
              )}
            </div>
          </Col>
        </Row>
        <div className="pb-3" style={{ maxHeight: 400, overflowY: "auto" }}>
          <RichTextEditorV2
            isEditing={true}
            value={richText}
            onChange={(val: string) => setRichText(val)}
            useStrikethrough={false}
            useMentions={true}
            useHeader={false}
            useColor={false}
            useBlock={false}
            onOpenBrieCompose={() => {
              setHide(true);
            }}
            onCloseBrieCompose={() => {
              callFunctionIfExists(onCloseBrieCompose);
              setHide(false);
            }}
            onVoiceHandler={() => callFunctionIfExists(onOpenBrieCompose)}
            hide={hide}
          />
        </div>
        {hide && (
          <BrieComposeBody
            page={page}
            setPage={setPage}
            generatedText={generatedText}
            setGeneratedText={setGeneratedText}
            providedText={richText}
            simplified={true}
          />
        )}

        {error && (
          <Alert
            className="px-2 py-1 position-absolute"
            style={{ right: 60, bottom: -8 }}
            color="danger"
          >
            {error}
          </Alert>
        )}
        {!hide && (
          <div className="d-flex justify-content-between position-relative">
            <Button
              onClick={() => {
                openComments();
                setOpenRecordCommentsDrawer(true);
                setKeepOpen(true);
              }}
              outline
              size="sm"
              color="primary"
            >
              View Comments
            </Button>
            <IconButton
              color="primary"
              icon="send-variant"
              onClick={CreateComment}
              loading={creatingComment}
              disabled={!richText}
              wide
            />
          </div>
        )}
      </div>
    </>
  );

  function CreateComment() {
    if (!richText) {
      setError("Please type a message to continue");
      setTimeout(() => setError(""), 3000);
      return;
    }

    const mentionedUsers = StrUtils.getMentionedUserIds(richText);

    createComment({
      comment: {
        comment: {
          RichText: richText,
          PlainText: StrUtils.convertMarkupToPlaintext(richText),
        },
        context: "Record",
        contextID: recordID,
        parentComment: undefined,
        mentionedUsers: mentionedUsers,
        commentCount: commentPages?.[0]?.totalComments || 0,
      },
      convoMetadata: {
        RecordName: recordName,
        RecordModule: moduleAPIName,
      },
      onSuccess: () => {
        setRichText("");
      },
      onError: setError,
    });
  }
};

export default CommentsWidget;
